import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Button, Typography, Table, Sheet, CircularProgress, Box, IconButton, Tooltip, Input } from "@mui/joy";
import { format, parseISO } from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import ChevronRight from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";

import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ModalContext";
import { supabase } from "../../supabaseClient";

import { fetchProfilePicture, getInitials } from "../../utils";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../Pagination";

import { Student as DBStudent, PostgresChangePayload, GradeLevelLabels } from "../../types";

import AddEditStudentModal from "../modals/AddEditStudentModal";

const STUDENT_PROFILE_PICTURES: string = "students";

type StudentWithProfilePicture = DBStudent & { profile_picture?: string | null };

function formatDateOfBirth(dateOfBirth: string | null) {
  if (!dateOfBirth) {
    return "";
  }

  try {
    // Parse as ISO date and format in local timezone
    const date = parseISO(dateOfBirth);
    return format(date, "MM/dd/yyyy");
  } catch (error) {
    console.error("Invalid date:", error);
    return "";
  }
}

function formatFirstNameDisplay(student: StudentWithProfilePicture) {
  if (student.preferred_first_name && student.preferred_first_name.trim() !== "") {
    return `${student.first_name} (${student.preferred_first_name})`;
  }
  return student.first_name;
}

const StudentsTable = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [students, setStudents] = useState<StudentWithProfilePicture[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { openModal } = useModal();

  // Use pagination hook
  const {
    paginatedData: paginatedStudents,
    currentPage,
    totalPages,
    totalItems,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
  } = usePagination({
    data: students,
    searchTerm: searchQuery,
    searchFields: ["first_name", "last_name", "preferred_first_name"],
  });

  // Fetch a single student with relationships and profile pictures
  const fetchStudentWithRelations = async (studentId: string) => {
    try {
      const { data, error } = await supabase
        .from("students")
        .select(`*`)
        .order("last_name")
        .order("first_name")
        .eq("id", studentId)
        .single(); // Fetch only one row

      if (error || !data) {
        throw error;
      }

      const profilePicture = await fetchProfilePicture(STUDENT_PROFILE_PICTURES, data.profile_picture_id);

      return {
        ...data,
        profile_picture: profilePicture,
      } as StudentWithProfilePicture;
    } catch (error) {
      console.error("Error fetching student with relations:", error);
      return null;
    }
  };

  // Fetch students from Supabase
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("students")
        .select(`*`)
        .order("last_name")
        .order("first_name");

      if (error) {
        throw error;
      }

      const formattedStudents: StudentWithProfilePicture[] = await Promise.all(
        data.map(async (student: DBStudent) => {
          const profilePicture = await fetchProfilePicture(STUDENT_PROFILE_PICTURES, student.profile_picture_id);

          return {
            ...student,
            profile_picture: profilePicture,
          };
        })
      );

      setStudents(formattedStudents);
    } catch (error) {
      showToast("Error fetching students.", "danger", "solid");
    } finally {
      setLoading(false);
    }
  };

  const handleStudentChange = async (payload: PostgresChangePayload<DBStudent>) => {
    if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
      const updatedStudent = await fetchStudentWithRelations(payload.new.id);
      if (updatedStudent) {
        setStudents((prevStudents) => {
          // Replace the existing student if it's an update, or add it if it's an insert
          const studentIndex = prevStudents.findIndex((student) => student.id === updatedStudent.id);
          if (studentIndex !== -1) {
            const updatedStudents = [...prevStudents];
            updatedStudents[studentIndex] = updatedStudent;
            return updatedStudents;
          } else {
            return [...prevStudents, updatedStudent];
          }
        });
      }
    } else if (payload.eventType === "DELETE") {
      setStudents((prevStudents) =>
        prevStudents.filter((student) => student.id !== payload.old.id)
      );
    }
  };

  useEffect(() => {
    // Fetch initial data.
    fetchStudents();

    // Subscribe to changes in the "students" table
    const subscription = supabase
      .channel("public:students")
      .on(
        "postgres_changes", 
        { event: "*", schema: "public", table: "students" }, 
        (payload) => {
          // console.log("Change received!", payload);
          handleStudentChange(payload as any);
        }
      )
      .subscribe();

    // Cleanup the subscription on component unmount
    return () => {
      supabase.removeChannel(subscription);
    };
    /* eslint-disable-next-line */
  }, []);

  // Open the AddEditStudentModal for adding a new student
  const handleAddStudent = () => {
    openModal(<AddEditStudentModal />, {
    });
  };

  const handleViewStudent = (student: StudentWithProfilePicture) => {
    navigate(`/students/${student.id}`);
  }

  return (
    <React.Fragment>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} gap={2}>
        <Input
          placeholder="Search students..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startDecorator={<SearchIcon />}
          sx={{ width: 300 }}
        />
        <Button startDecorator={<AddIcon />} onClick={handleAddStudent} sx={{ ml: 2, minWidth: 140 }}>
          Add Student
        </Button>
      </Box>
      <Sheet variant="outlined" sx={{ width: "100%", borderRadius: "sm", overflow: "auto", minHeight: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : paginatedStudents.length > 0 ? (
          <>
            <Table>
              <thead>
                <tr>
                  {/* This is an empty header for their photo */}
                  <th style={{ width: 64 }}></th>
                  <th>Last Name</th>
                  <th>First Name</th>
                  <th>Grade</th>
                  <th>DoB</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedStudents.map((studentItem: StudentWithProfilePicture) => (
                  <tr key={studentItem.id}>
                    <td style={{ verticalAlign: "middle", width: 48 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          key={`student-avatar-${studentItem.id}`}
                          src={studentItem.profile_picture || undefined} // Use the profile picture directly from student data
                          sx={{ width: 32, height: 32 }}
                        >
                          {!studentItem.profile_picture ? getInitials(studentItem) : null}
                        </Avatar>
                      </Box>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>         
                      <Typography>{studentItem.last_name}</Typography>
                    </td> 
                    <td style={{ verticalAlign: "middle" }}>
                      <Typography>{formatFirstNameDisplay(studentItem)}</Typography>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <Typography>{GradeLevelLabels[studentItem.grade_level as import("../../types/Grade").GradeLevel] || "Not Set"}</Typography>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <Typography>{formatDateOfBirth(studentItem.date_of_birth)}</Typography>
                    </td>
                    <td>
                      <Tooltip title="View Student">
                        <IconButton onClick={() => handleViewStudent(studentItem)}>
                          <ChevronRight />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              rowsPerPage={rowsPerPage}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          </>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
            <Typography level="body-lg" color="neutral">
              {searchQuery.trim() ? "No students found matching your search." : "No Students"}
            </Typography>
          </Box>
        )}
      </Sheet>
    </React.Fragment>
  );
};

export default StudentsTable;
